import { ResponsiveStyleValue, Theme, ThemeUICSSObject } from 'theme-ui';
import { responsive } from '../../util';
import getColorsAndStatesByPath, {
  TGetColorsAndStatesOptions,
} from './util/getColorsAndStatesByPath';

/* eslint-disable @typescript-eslint/naming-convention */
export function isColorDefinition(
  value:
    | IColorDefinition
    | TColorModifier
    | Record<string, unknown>
    | string
    | number
    | TActionDefinition
    | TStatesRetriever
    | ((index: number) => ResponsiveStyleValue<number>)
    | typeof responsive,
): value is IColorDefinition {
  return !!value && typeof value === 'object' && 'main' in value;
}

export interface IColorDefinition {
  main: string;
  light?: string;
  dark?: string;
  contrastText?: string;
}

export interface IActionOpacity {
  light: string;
  dark: string;
}

export interface TStates {
  active?: boolean;
  checked?: boolean;
  default?: boolean;
  disabled?: boolean;
  focus?: boolean;
  hover?: boolean;
  readonly?: boolean;
  selected?: boolean;
}

export type TGetColorStateDefinition = Pick<
  ThemeUICSSObject,
  | 'backgroundColor'
  | 'borderColor'
  | 'borderLeftColor'
  | 'borderRightColor'
  | 'borderBottomColor'
  | 'borderTopColor'
  | 'color'
>;

export type TParsedPaletteColorStateRetriever = (
  color: string,
  state: keyof TStates,
) => string;

export type TColorStateRetriever = (
  color: string,
  state: keyof TStates,
  palette: TParsedPalette,
) => string;

export type TParsedPaletteStateRetriever = (
  definition: Partial<TGetColorStateDefinition>,
  state: keyof TStates,
) => TGetColorStateDefinition;

export type TStateRetriever = (
  definition: Partial<TGetColorStateDefinition>,
  state: keyof TStates,
  palette: TParsedPalette,
) => TGetColorStateDefinition;

export type TParsedPaletteStatesRetriever = (
  definition: Partial<TGetColorStateDefinition>,
  states?: TStates,
) => ThemeUICSSObject;

export type TStatesRetriever = (
  definition: Partial<TGetColorStateDefinition>,
  palette: TParsedPalette,
  states?: TStates,
) => ThemeUICSSObject;

export type TParsedPaletteStatesByDefinition = (
  definition: Partial<TGetColorStateDefinition>,
  options?: TGetColorsAndStatesOptions,
) => ThemeUICSSObject;

export type TStatesByDefinition = (
  definition: Partial<TGetColorStateDefinition>,
  theme: Theme,
  options?: TGetColorsAndStatesOptions,
) => ThemeUICSSObject;

export type TApplyStates = (
  properties: Record<
    string,
    TColorDefinition | string | Record<string, unknown>
  >[],
  theme: Theme,
  states?: TStates,
) => void;

export interface TActionDefinition<
  OpacityColor = undefined | string | IActionOpacity,
> {
  active: OpacityColor;
  activeOpacity: number;
  disabled: OpacityColor;
  disabledOpacity: number;
  focus: OpacityColor;
  focusOpacity: number;
  hover: OpacityColor;
  hoverOpacity: number;
  readonly: OpacityColor;
  readonlyOpacity: number;
  selected: OpacityColor;
  selectedOpacity: number;
}

export type TColorModifier = (color: string, ratio?: number) => string;
export interface TPalette<
  ColorDefinition = IColorDefinition,
  ColorModifier = TColorModifier,
  ActionDefinition = TActionDefinition,
> {
  action: ActionDefinition;
  background: {
    default: string;
    overlay: string;
    paper: string;
  };
  border: {
    article: string;
    field: string;
    ghost: string;
    section: string;
  };
  common: {
    white: string;
    black: string;
  };
  error: ColorDefinition;
  gray?: {
    100: string;
    150: string;
    200: string;
    250: string;
    300: string;
    350: string;
    400: string;
    450: string;
    500: string;
    550: string;
    600: string;
    650: string;
    700: string;
    750: string;
    800: string;
    850: string;
    900: string;
    950: string;
  };
  info: ColorDefinition;
  primary: ColorDefinition;
  secondary: ColorDefinition;
  spacing: (index: number) => ResponsiveStyleValue<number>;
  success: ColorDefinition;
  text: {
    accent: string;
    disabled: string;
    icon: string;
    link: string;
    primary: string;
    secondary: string;
    title: string;
  };
  warning: ColorDefinition;

  lightenRatio?: number;
  darkenRatio?: number;
  /**
   * Acepta un ratio entre 0 y 100
   */
  lightenColor?: ColorModifier;
  /**
   * Acepta un ratio entre 0 y 100
   */
  darkenColor?: ColorModifier;
  getContrastText?: ColorModifier;
  responsive: typeof responsive;

  queryColumnsMultiplier?: number;
}

export type TBuildStateObject = <T extends Partial<ThemeUICSSObject>>(
  props: T,
  state: keyof Required<TStates> | 'checked',
) => ThemeUICSSObject;

export type TBuildColorsObject = (
  definition: Partial<TGetColorStateDefinition>,
) => TGetColorStateDefinition;

export type TParsedPalette = Required<
  TPalette<
    Required<IColorDefinition>,
    TColorModifier,
    Required<TActionDefinition<IActionOpacity>>
  >
> & {
  applyStates: TApplyStates;
  buildColorsObject: TBuildColorsObject;
  buildStateObject: TBuildStateObject;
  getColor: TParsedPaletteColorStateRetriever;
  getColorStates: TParsedPaletteStatesRetriever;
  getOneState: TParsedPaletteStateRetriever;
  getStatesForColors: TParsedPaletteStatesRetriever;
  getStatesFromDefinition: TParsedPaletteStatesByDefinition;
  getStatesFromPath: typeof getColorsAndStatesByPath;
  getSelectedColors: () => ThemeUICSSObject;
  getColorsByPath: (
    path: string,
    options?: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      mergeObject?: Record<string, any>;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => TGetColorStateDefinition & Record<string, any>;
};

export type TCustomPalette = Partial<TPalette> &
  Partial<
    Pick<
      TParsedPalette,
      | 'applyStates'
      | 'buildColorsObject'
      | 'buildStateObject'
      | 'getColor'
      | 'getColorStates'
      | 'getSelectedColors'
      | 'getContrastText'
      | 'getOneState'
      | 'getStatesForColors'
      | 'getStatesFromDefinition'
      | 'getStatesFromPath'
    >
  >;

export type TColorDefinitionPrimitive = Pick<
  ThemeUICSSObject,
  'color' | 'backgroundColor' | 'borderColor'
>;

export type TColorDefinition = TColorDefinitionPrimitive & {
  active?: TColorDefinitionPrimitive;
  checked?: TColorDefinitionPrimitive;
  disabled?: TColorDefinitionPrimitive;
  focus?: TColorDefinitionPrimitive;
  hover?: TColorDefinitionPrimitive;
  preventParse?: boolean;
  selected?: TColorDefinitionPrimitive;
};

declare global {
  interface Window {
    currentPalette: TParsedPalette;
    customPalettes?: TPalette[];
    defaultPalette?: TPalette;
  }
}

export type TThemeModifier = (theme: Theme) => Theme;
